import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Check, 
  ArrowLeft,
  CalendarClock,
  BarChart3,
  Users,
  Send, 
  X, 
  ShieldCheck, 
  Lock, 
  Target, 
  TrendingUp,
  MessageSquareOff, // Icono para WhatsApp individual, sin control
  MessagesSquare, // Icono para WhatsApp API, centralizado
  FileWarning, // Pérdida de datos
  Database, // Control de datos
  Scale, // Trazabilidad y Calidad
  Bot, // Automatización
  Calculator, // Icono para la calculadora
  MessageSquareWarning, // Bloqueo
  FileTextIcon, // Plantillas
  Link, // Integración
  Info, // Nuevo icono para matices de política
  PhoneOff, // Causa de bloqueo
  AlertTriangle, // Causa de bloqueo
} from 'lucide-react';

import ContactForm from '../ContactForm';
import ParticlesBackground from '../components/ParticlesBackground';
import LanguageSwitcher from '../components/LanguageSwitcher';

// --- COMPONENTES DE PÁGINA REUTILIZADOS ---

const PageHeader = ({ onOpenPopup, t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img 
          src="https://imgur.com/nYWIvWJ.png" 
          alt="Nextcoders Logo" 
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; }}
        />
      </a>
      <div className="flex items-center gap-4">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {t('common.free_diagnostic')}
        </button>
      </div>
    </div>
  </header>
);

// Componente reutilizable para tarjetas de características
const FeatureCard = ({ icon: Icon, title, text, colorClass, bgColorClass = "bg-gray-50", borderColorClass = "border-gray-200" }) => (
  <div className={`${bgColorClass} ${borderColorClass} border rounded-lg p-5 h-full transition hover:shadow-md`}>
    <div className="flex items-center gap-3 mb-2">
      <Icon className={colorClass} size={24} />
      <h4 className="font-semibold text-[#141414]">{title}</h4>
    </div>
    <p className="text-sm text-gray-700">{text}</p>
  </div>
);

// --- COMPONENTE DE CALCULADORA DE PRECIOS ---

const waPrices = {
  argentina: 	{ marketing: 0.0743, utility: 0.0459, auth: 0.0417 },
  brasil: 	 	{ marketing: 0.0632, utility: 0.0382, auth: 0.0346 },
  chile: 	 	{ marketing: 0.0722, utility: 0.0437, auth: 0.0397 },
  colombia: 	{ marketing: 0.0100, utility: 0.0080, auth: 0.0072 },
  mexico: 	 	{ marketing: 0.0341, utility: 0.0227, auth: 0.0207 },
  peru: 	 	{ marketing: 0.0227, utility: 0.0136, auth: 0.0124 },
  usa: 	 	  { marketing: 0.0147, utility: 0.0098, auth: 0.0090 },
  latam: 	 	{ marketing: 0.0450, utility: 0.0270, auth: 0.0250 }
};

const getCountryName = (code, t) => {
  const countries = t('whatsapp.calculator.countries', { returnObjects: true });
  return countries[code] || code;
};

const getCategoryName = (code, t) => {
  const categories = t('whatsapp.calculator.categories', { returnObjects: true });
  return categories[code] || code;
};

const WACalculator = ({ t }) => {
  const [country, setCountry] = useState('mexico');
  const [category, setCategory] = useState('marketing');
  const [qty, setQty] = useState(1);
  const [result, setResult] = useState(null);

  const calcWAPrice = () => {
    const quantity = parseInt(qty) || 1;
    const pricePerUnit = waPrices[country][category];
    const total = (pricePerUnit * quantity).toFixed(4);
    
    setResult({
      total: total,
      detail: t('whatsapp.calculator.result_detail', {
        qty: quantity,
        suffix: quantity > 1 ? 's' : '',
        country: getCountryName(country, t),
        category: getCategoryName(category, t)
      })
    });
  };

  return (
    <section className="bg-white rounded-xl p-8 shadow-inner border border-green-100 mt-16" id="calculadora">
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-green-700 mb-2 flex items-center justify-center gap-3">
          <Calculator size={30} /> {t('whatsapp.calculator.title')}
        </h2>
        <p className="text-gray-600">
          {t('whatsapp.calculator.subtitle')}
        </p>
        <small className="text-gray-500">
          {t('whatsapp.calculator.source')}{' '}
          <a href="https://business.whatsapp.com/products/platform-pricing?lang=es_LA" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">{t('whatsapp.calculator.source_link')}</a>
        </small>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {/* País */}
        <div className="flex flex-col">
          <label htmlFor="wa-country" className="font-semibold text-gray-700 mb-2">{t('whatsapp.calculator.country_label')}</label>
          <select 
            id="wa-country" 
            value={country} 
            onChange={(e) => setCountry(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500 transition"
          >
            <option value="argentina">{t('whatsapp.calculator.countries.argentina')}</option>
            <option value="brasil">{t('whatsapp.calculator.countries.brasil')}</option>
            <option value="chile">{t('whatsapp.calculator.countries.chile')}</option>
            <option value="colombia">{t('whatsapp.calculator.countries.colombia')}</option>
            <option value="mexico">{t('whatsapp.calculator.countries.mexico')}</option>
            <option value="peru">{t('whatsapp.calculator.countries.peru')}</option>
            <option value="usa">{t('whatsapp.calculator.countries.usa')}</option>
            <option value="latam">{t('whatsapp.calculator.countries.latam')}</option>
          </select>
        </div>

        {/* Categoría */}
        <div className="flex flex-col">
          <label htmlFor="wa-category" className="font-semibold text-gray-700 mb-2">{t('whatsapp.calculator.category_label')}</label>
          <select 
            id="wa-category" 
            value={category} 
            onChange={(e) => setCategory(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500 transition"
          >
            <option value="marketing">{t('whatsapp.calculator.categories.marketing')}</option>
            <option value="utility">{t('whatsapp.calculator.categories.utility')}</option>
            <option value="auth">{t('whatsapp.calculator.categories.auth')}</option>
          </select>
        </div>

        {/* Cantidad */}
        <div className="flex flex-col">
          <label htmlFor="wa-qty" className="font-semibold text-gray-700 mb-2">{t('whatsapp.calculator.qty_label')}</label>
          <input 
            type="number" 
            id="wa-qty" 
            min="1" 
            value={qty} 
            onChange={(e) => setQty(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500 transition"
          />
        </div>
      </div>
      
      <div className="text-center">
        <button 
          onClick={calcWAPrice}
          className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-3 rounded-full hover:bg-green-700 transition font-semibold shadow-md transform hover:scale-[1.02]"
        >
          {t('whatsapp.calculator.btn')} <Calculator size={18} />
        </button>
      </div>

      {result && (
        <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 mt-6 text-center animate-fadeIn">
          <p className="text-xl text-green-800 font-bold mb-1">
            {t('whatsapp.calculator.total_estimated')} <span className="text-2xl font-extrabold">${result.total} USD</span>
          </p>
          <p className="text-sm text-green-700">{result.detail}</p>
        </div>
      )}

      <div className="mt-6 text-sm text-gray-500 space-y-2">
        <p className="flex items-start gap-2"><Check size={16} className="text-green-500 flex-shrink-0"/> {t('whatsapp.calculator.notes.service')}</p>
        <p className="flex items-start gap-2"><Check size={16} className="text-green-500 flex-shrink-0"/> {t('whatsapp.calculator.notes.marketing')}</p>
        <p className="flex items-start gap-2"><Check size={16} className="text-green-500 flex-shrink-0"/> {t('whatsapp.calculator.notes.utility')}</p>
      </div>
    </section>
  );
};


const WhatsappPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden'; 
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto'; 
  };

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <ParticlesBackground />
      
      {/* Popup Modal para Bitrix24 */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label={t('whatsapp.popup_close')}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('common.diagnostic_request')}</h3>
              <p className="text-gray-600 mb-6">{t('common.diagnostic_desc')}</p>
              <ContactForm 
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}
      
      <PageHeader onOpenPopup={openPopup} t={t} />

      {/* Fondo de partículas con mayor contraste en fondo claro */}


      {/* Contenido Principal */}
      <main className="relative z-10 pt-32 pb-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
          
          {/* Botón Volver */}
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          {/* Hero de la Página */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-4">
              {t('whatsapp.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              {t('whatsapp.hero.subtitle')}
            </p>
          </div>

          {/* Sección Comparativa: Antes (Problema) y Después (Solución) */}
          <div className="grid md:grid-cols-2 gap-12">
            
            {/* Columna: ANTES (El Problema del WhatsApp Personal) */}
            <div>
              <div className="flex items-center justify-center bg-red-100 text-red-700 w-16 h-16 rounded-full mx-auto mb-6 shadow-md">
                <MessageSquareOff size={32} />
              </div>
              <h2 className="text-2xl font-bold text-center text-[#141414] mb-6">
                {t('whatsapp.comparison.chaos_title')}
              </h2>
              <p className="text-center text-gray-600 mb-8">
                {t('whatsapp.comparison.chaos_subtitle')}
              </p>

              <div className="space-y-6">
                <FeatureCard
                  icon={Lock}
                  colorClass="text-red-600"
                  title={t('whatsapp.comparison.chaos_features.security.title')}
                  text={t('whatsapp.comparison.chaos_features.security.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-red-200"
                />
                <FeatureCard
                  icon={FileWarning}
                  colorClass="text-red-600"
                  title={t('whatsapp.comparison.chaos_features.data_loss.title')}
                  text={t('whatsapp.comparison.chaos_features.data_loss.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-red-200"
                />
                <FeatureCard
                  icon={Users}
                  colorClass="text-red-600"
                  title={t('whatsapp.comparison.chaos_features.traceability.title')}
                  text={t('whatsapp.comparison.chaos_features.traceability.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-red-200"
                />
                <FeatureCard
                  icon={BarChart3}
                  colorClass="text-red-600"
                  title={t('whatsapp.comparison.chaos_features.scaling.title')}
                  text={t('whatsapp.comparison.chaos_features.scaling.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-red-200"
                />
              </div>
            </div>

            {/* Columna: DESPUÉS (La Solución con WhatsApp Business API) */}
            <div>
              <div className="flex items-center justify-center bg-green-100 text-green-700 w-16 h-16 rounded-full mx-auto mb-6 shadow-md">
                <MessagesSquare size={32} />
              </div>
              <h2 className="text-2xl font-bold text-center text-[#141414] mb-6">
                {t('whatsapp.comparison.control_title')}
              </h2>
              <p className="text-center text-gray-600 mb-8">
                {t('whatsapp.comparison.control_subtitle')}
              </p>

              <div className="space-y-6">
                <FeatureCard
                  icon={ShieldCheck}
                  colorClass="text-green-600"
                  title={t('whatsapp.comparison.control_features.security.title')}
                  text={t('whatsapp.comparison.control_features.security.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-green-200"
                />
                <FeatureCard
                  icon={Database}
                  colorClass="text-green-600"
                  title={t('whatsapp.comparison.control_features.crm.title')}
                  text={t('whatsapp.comparison.control_features.crm.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-green-200"
                />
                <FeatureCard
                  icon={Scale}
                  colorClass="text-green-600"
                  title={t('whatsapp.comparison.control_features.traceability.title')}
                  text={t('whatsapp.comparison.control_features.traceability.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-green-200"
                />
                <FeatureCard
                  icon={Bot}
                  colorClass="text-green-600"
                  title={t('whatsapp.comparison.control_features.automation.title')}
                  text={t('whatsapp.comparison.control_features.automation.text')}
                  bgColorClass="bg-white"
                  borderColorClass="border-green-200"
                />
              </div>
            </div>
          </div>
          
          {/* SECCIÓN NUEVA: ¿POR QUÉ TE BLOQUEAN? (Formato de tabla/grid más limpio) */}
          <section className="mt-20 p-8 bg-white rounded-xl shadow-lg border-l-4 border-red-500">
            <h2 className="text-3xl font-bold text-[#141414] mb-6 flex items-center gap-3">
              <MessageSquareWarning size={32} className="text-red-500"/> {t('whatsapp.blocking.title')}
            </h2>
            <p className="text-gray-700 mb-6 max-w-3xl">
              {t('whatsapp.blocking.subtitle')}
            </p>
            
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-sm text-gray-700 p-4 rounded-lg bg-red-50 border border-red-200">
                
                <div className="flex items-start gap-3">
                    <Lock size={20} className="text-red-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong className="text-[#141414]">{t('whatsapp.blocking.features.spam.title')}</strong>
                        <p className="text-gray-600">{t('whatsapp.blocking.features.spam.text')}</p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <PhoneOff size={20} className="text-red-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong className="text-[#141414]">{t('whatsapp.blocking.features.unofficial_apps.title')}</strong>
                        <p className="text-gray-600">{t('whatsapp.blocking.features.unofficial_apps.text')}</p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong className="text-[#141414]">{t('whatsapp.blocking.features.deceptive_content.title')}</strong>
                        <p className="text-gray-600">{t('whatsapp.blocking.features.deceptive_content.text')}</p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <Users size={20} className="text-red-600 flex-shrink-0 mt-1"/>
                    <div>
                        <strong className="text-[#141414]">{t('whatsapp.blocking.features.user_reports.title')}</strong>
                        <p className="text-gray-600">{t('whatsapp.blocking.features.user_reports.text')}</p>
                    </div>
                </div>
            </div>
          </section>

          {/* COMPONENTE CALCULADORA (Integrado aquí) */}
          <WACalculator t={t} />

          {/* SECCIÓN 1: PLANTILLAS PREAPROBADAS Y COSTO (Tarjeta horizontal separada) */}
          <section className="mt-20 p-8 bg-white rounded-xl shadow-lg border-l-4 border-green-500">
            <h3 className="text-2xl font-bold text-[#141414] mb-4 flex items-center gap-2">
              <FileTextIcon size={24} className="text-green-600"/> {t('whatsapp.templates.title')}
            </h3>
            <p className="text-gray-700 mb-4">
              {t('whatsapp.templates.subtitle')}
            </p>
            
            <h4 className="font-bold text-lg text-green-700 mb-2">{t('whatsapp.templates.categories_title')}</h4>
            <ul className="space-y-2 text-sm text-gray-600 p-3 bg-green-50 rounded-md">
              <li className="flex items-center gap-2">
                <Check size={18} className="text-green-500 flex-shrink-0"/> 
                {t('whatsapp.templates.categories.marketing')}
              </li>
              <li className="flex items-center gap-2">
                <Check size={18} className="text-green-500 flex-shrink-0"/> 
                {t('whatsapp.templates.categories.utility')}
              </li>
              <li className="flex items-center gap-2">
                <Check size={18} className="text-green-500 flex-shrink-0"/> 
                {t('whatsapp.templates.categories.service')}
              </li>
            </ul>
          </section>
          
          {/* SECCIÓN 2: INTEGRACIÓN TOTAL CON TU CRM (Tarjeta horizontal separada) */}
          <section className="mt-8 p-8 bg-white rounded-xl shadow-lg border-l-4 border-[#141414]">
            <h3 className="text-2xl font-bold text-[#141414] mb-4 flex items-center gap-2">
              <Link size={24} className="text-[#141414]"/> {t('whatsapp.crm.title')}
            </h3>
            <p className="text-gray-700 mb-4">
              {t('whatsapp.crm.subtitle')}
            </p>
            <ul className="space-y-2 text-sm text-gray-600 p-3 bg-gray-100 rounded-md">
              <li className="flex items-center gap-2">
                <Check size={18} className="text-green-500 flex-shrink-0"/> 
                {t('whatsapp.crm.features.traceability')}
              </li>
              <li className="flex items-center gap-2">
                <Check size={18} className="text-green-500 flex-shrink-0"/> 
                {t('whatsapp.crm.features.automation')}
              </li>
              <li className="flex items-center gap-2">
                <Check size={18} className="text-green-500 flex-shrink-0"/> 
                {t('whatsapp.crm.features.compatible_systems')}
              </li>
            </ul>
          </section>


          {/* SECCIÓN NUEVA: MATICES DE LA POLÍTICA Y COMPLIANCE (Fondo amarillo completo y limpio) */}
          <section className="mt-16 p-6 bg-yellow-100 rounded-xl shadow-lg border-l-4 border-yellow-500">
            <h3 className="text-2xl font-bold text-[#141414] mb-4 flex items-center gap-2">
                <Info size={24} className="text-yellow-700"/> {t('whatsapp.compliance.title')}
            </h3>
            <p className="text-gray-700 mb-6">
                {t('whatsapp.compliance.subtitle')}
            </p>
            
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6 text-sm text-gray-700">
                
                <div className="flex items-start gap-3">
                    <TrendingUp size={20} className="text-yellow-700 flex-shrink-0 mt-1"/>
                    <div>
                        <strong className="text-[#141414]">{t('whatsapp.compliance.features.portfolio_limits.title')}</strong>
                        <p className="text-gray-600">{t('whatsapp.compliance.features.portfolio_limits.text')}</p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <CalendarClock size={20} className="text-yellow-700 flex-shrink-0 mt-1"/>
                    <div>
                        <strong className="text-[#141414]">{t('whatsapp.compliance.features.window_24h.title')}</strong>
                        <p className="text-gray-600">{t('whatsapp.compliance.features.window_24h.text')}</p>
                    </div>
                </div>
                
                <div className="flex items-start gap-3">
                    <Target size={20} className="text-yellow-700 flex-shrink-0 mt-1"/>
                    <div>
                        <strong className="text-[#141414]">{t('whatsapp.compliance.features.marketing_restriction.title')}</strong>
                        <p className="text-gray-600">{t('whatsapp.compliance.features.marketing_restriction.text')}</p>
                    </div>
                </div>

                <div className="flex items-start gap-3">
                    <Link size={20} className="text-yellow-700 flex-shrink-0 mt-1"/>
                    <div>
                        <strong className="text-[#141414]">{t('whatsapp.compliance.features.app_vs_api.title')}</strong>
                        <p className="text-gray-600">{t('whatsapp.compliance.features.app_vs_api.text')}</p>
                    </div>
                </div>
            </div>
          </section>

          {/* CTA FINAL */}
          <section className="max-w-4xl mx-auto mt-24">
            <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl p-8 md:p-12 text-center shadow-lg">
              <h2 className="text-3xl font-bold mb-4">
                {t('whatsapp.final_cta.title')}
              </h2>
              <p className="text-lg opacity-90 max-w-xl mx-auto mb-8">
                {t('whatsapp.final_cta.subtitle')}
              </p>
              <button
                onClick={openPopup}
                className="inline-flex items-center justify-center gap-2 bg-[#141414] text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition font-semibold shadow-xl transform hover:scale-[1.02]"
              >
                <Send size={18} />
                {t('whatsapp.final_cta.btn')}
              </button>
            </div>
          </section>

        </div>
      </main>

      
    </div>
  );
};

export default WhatsappPage;

